'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { FilterSection } from '@/components/parts/voice/FilterSection'
import { SelectedFilters } from '@/components/parts/voice/SelectedFilters'
import { VoiceGrid } from '@/components/parts/voice/VoiceGrid'
import {
  Voice,
  Industries,
  Departments,
  Scales,
  DocumentTypes
} from '@/types/newt/voice'
import { Department } from '@/types/newt/department'

interface VoiceContentsProps {
  industries: Industries[]
  departments: Departments[]
  scales: Scales[]
  documentTypes: DocumentTypes[]
  voice: Voice[]
  department: Department[]
}

export function VoiceListContents({
  industries,
  departments,
  scales,
  documentTypes,
  voice,
  department
}: VoiceContentsProps) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([])
  const [selectedScales, setSelectedScales] = useState<string[]>([])
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([])
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<string[]>(
    []
  )

  const filteredVoice = useMemo(() => {
    return voice.filter((v) => {
      const industrySlugs = Array.isArray(v.industry)
        ? v.industry.map((ind) => ind.slug)
        : [v.industry.slug]
      const departmentSlugs = Array.isArray(v.department)
        ? v.department.map((dep) => dep.slug)
        : [v.department.slug]
      const documentTypeSlugs = Array.isArray(v.documentType)
        ? v.documentType.map((doc) => doc.slug)
        : [v.documentType.slug]

      return (
        (selectedIndustries.length === 0 ||
          selectedIndustries.some((ind) => industrySlugs.includes(ind))) &&
        (selectedScales.length === 0 ||
          selectedScales.includes(v.scale.slug)) &&
        (selectedDepartments.length === 0 ||
          selectedDepartments.some((dep) => departmentSlugs.includes(dep))) &&
        (selectedDocumentTypes.length === 0 ||
          selectedDocumentTypes.some((doc) => documentTypeSlugs.includes(doc)))
      )
    })
  }, [
    voice,
    selectedIndustries,
    selectedScales,
    selectedDepartments,
    selectedDocumentTypes
  ])

  useEffect(() => {
    const industriesQuery = searchParams?.get('industries')?.split(',') || []
    const scalesQuery = searchParams?.get('scales')?.split(',') || []
    const departmentsQuery = searchParams?.get('departments')?.split(',') || []
    const documentTypesQuery =
      searchParams?.get('documentTypes')?.split(',') || []

    setSelectedIndustries(industriesQuery)
    setSelectedScales(scalesQuery)
    setSelectedDepartments(departmentsQuery)
    setSelectedDocumentTypes(documentTypesQuery)
  }, [searchParams])

  const handleFilter = () => {
    const queryParams = new URLSearchParams()
    if (selectedIndustries.length)
      queryParams.set('industries', selectedIndustries.join(','))
    if (selectedScales.length)
      queryParams.set('scales', selectedScales.join(','))
    if (selectedDepartments.length)
      queryParams.set('departments', selectedDepartments.join(','))
    if (selectedDocumentTypes.length)
      queryParams.set('documentTypes', selectedDocumentTypes.join(','))

    router.push(`/voice?${queryParams.toString()}`)
  }

  return (
    <div className="px-6 pt-12 lg:pt-16 pb-16 lg:pb-32">
      <div className="max-w-[1200px] mx-auto">
        <FilterSection
          industries={industries}
          departments={departments}
          scales={scales}
          documentTypes={documentTypes}
          selectedIndustries={selectedIndustries}
          setSelectedIndustries={setSelectedIndustries}
          selectedScales={selectedScales}
          setSelectedScales={setSelectedScales}
          selectedDepartments={selectedDepartments}
          setSelectedDepartments={setSelectedDepartments}
          selectedDocumentTypes={selectedDocumentTypes}
          setSelectedDocumentTypes={setSelectedDocumentTypes}
          handleFilter={handleFilter}
          filteredVoiceCount={filteredVoice.length}
        />

        <SelectedFilters
          selectedIndustries={selectedIndustries}
          selectedDepartments={selectedDepartments}
          selectedScales={selectedScales}
          selectedDocumentTypes={selectedDocumentTypes}
          industries={industries}
          departments={departments}
          scales={scales}
          documentTypes={documentTypes}
        />

        <VoiceGrid filteredVoice={filteredVoice} />
      </div>
    </div>
  )
}
